import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

export type Tier = 'free' | 'pro';

export interface SubscriptionLimits {
  children: number;
  photosPerMilestone: number;
  voiceNotes: boolean;
  pdfExport: boolean;
  unlimitedRedditPulse: boolean;
}

const FREE_LIMITS: SubscriptionLimits = {
  children: 1,
  photosPerMilestone: 0,
  voiceNotes: false,
  pdfExport: false,
  unlimitedRedditPulse: false,
};

const PRO_LIMITS: SubscriptionLimits = {
  children: 5,
  photosPerMilestone: 20,
  voiceNotes: true,
  pdfExport: true,
  unlimitedRedditPulse: true,
};

export function useSubscription() {
  const [tier, setTier] = useState<Tier>('free');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user ?? null;
      if (!user) {
        if (!cancelled) { setTier('free'); setLoading(false); }
        return;
      }
      const { data } = await supabase
        .from('profiles')
        .select('subscription_tier, subscription_expires_at')
        .eq('id', user.id)
        .maybeSingle();
      if (cancelled) return;
      const t = (data?.subscription_tier as Tier) ?? 'free';
      const notExpired =
        !data?.subscription_expires_at ||
        new Date(data.subscription_expires_at).getTime() > Date.now();
      setTier(t === 'pro' && notExpired ? 'pro' : 'free');
      setLoading(false);
    };
    load();
    const { data: sub } = supabase.auth.onAuthStateChange(() => load());
    return () => { cancelled = true; sub.subscription.unsubscribe(); };
  }, []);

  return {
    tier,
    isPro: tier === 'pro',
    isFree: tier === 'free',
    limits: tier === 'pro' ? PRO_LIMITS : FREE_LIMITS,
    loading,
  };
}
